import { Stack, Typography } from '@mui/material';
import Fuse from 'fuse.js';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCornTechnologies } from '/@/hooks/catalog';
import { _ } from '/@/locale';
import { MuiAutocomplete } from '/@/mui/autocomplete';
import { getSessionBrands, getSessionCountry } from '/@/store/session/selectors';
import FieldviewAutofillIcon from './FieldviewAutofillIcon';

interface TechnologySelectorProps {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    disabled: boolean;
    error?: string;
    isFromFieldview?: boolean;
    autofillQuery?: string;
    onAutofilled?: () => void;
}

const TechnologySelector = ({
    value,
    onChange,
    disabled,
    error,
    isFromFieldview,
    autofillQuery,
    onAutofilled,
}: TechnologySelectorProps) => {
    const country = useSelector(getSessionCountry);
    const sessionBrands = useSelector(getSessionBrands);

    const technologiesQuery = useCornTechnologies({
        page: 1,
        pageSize: 10000,
        brand: sessionBrands['corn']?.[0]?.codename,
        country: country?.code,
    });

    const technologyOptions = useMemo(
        () => (technologiesQuery.data?.results || []).filter((t: any) => t.displayInCatalog),
        [technologiesQuery.data?.results]
    );

    useEffect(() => {
        if (!autofillQuery || !technologyOptions.length || value !== undefined) return;
        const query = autofillQuery.toLowerCase();
        // Technology names (e.g. "RR2") are short and typically appear inside the
        // seed product name (e.g. "DK 72-70 RR2"), so check substring first.
        const substringMatch = (technologyOptions as { id: number; name?: string }[]).find(
            (t) => t.name && query.includes(t.name.toLowerCase())
        );
        if (substringMatch) {
            onChange(substringMatch.id);
            onAutofilled?.();
            return;
        }
        // Fallback: fuzzy match the seed name against technology names.
        const fuse = new Fuse(technologyOptions as { id: number; name?: string }[], {
            keys: ['name'],
            threshold: 0.7,
            useTokenSearch: true,
            isCaseSensitive: false,
        });
        const match = fuse.search(autofillQuery)[0]?.item;
        if (match) {
            onChange(match.id);
            onAutofilled?.();
        }
    }, [technologyOptions, autofillQuery, value, onChange, onAutofilled]);

    return (
        <Stack spacing={0.5} sx={{ width: '100%' }}>
            <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                {_('Tecnología')}
            </Typography>
            <MuiAutocomplete
                disableClearable
                placeholder={_('Indicá la tecnología')}
                disabled={technologiesQuery.isValidating || disabled}
                value={value}
                onChange={(_: any, option: any) => onChange(option?.id)}
                options={technologyOptions}
                loading={technologiesQuery.isValidating}
                getOptionLabel={(option: any) => option.name || ''}
                getOptionValue={(option: any) => option.id}
                error={!!error}
                helperText={error}
                data-testid='field-monitoring-creation-dialog-technology-selector'
                endAdornment={isFromFieldview ? <FieldviewAutofillIcon /> : undefined}
            />
        </Stack>
    );
};

export default TechnologySelector;
